import React, { type JSX } from 'react'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { spacing } from '@mobi/component-library/Foundations'
import { Grid, GridCell, GridRow } from '@mobi/component-library/Legacy/Grid'
import { Icon as LegacyIcon } from '@mobi/component-library/Legacy/Icon'
import { hexColors } from '@mobi/settings/hexColors'
import { Button } from '@core/Components/Buttons/Button'
import { CalendarDatePicker } from '@core/Components/DatePicker/DatePicker'

interface Props {
  date: Date
  onDateChange: (date: Date) => void
}

export const DateNavigation: React.FC<Props> = ({ date, onDateChange }) => {
  const dateString = dayjs(date).format('ddd MMM Do')
  const nextDay = () => {
    closeCalendar()
    return dayjs(date).add(1, 'day').toDate()
  }

  const prevDay = () => {
    closeCalendar()
    return dayjs(date).subtract(1, 'day').toDate()
  }

  const onValueChange = (value: Date) => {
    closeCalendar()
    onDateChange(value)
  }

  const [showCalendar, setShowCalendar] = React.useState(false)

  const toggleCalendar = () => {
    setShowCalendar(!showCalendar)
  }

  const closeCalendar = () => {
    setShowCalendar(false)
  }

  return (
    <div style={{ paddingBottom: spacing.sm }}>
      <Grid>
        <GridRow>
          <GridCell width='15%' valign='middle' align='right'>
            <Button
              data-testid='date-nav-prev'
              color='secondary'
              type='button'
              onClick={() => onDateChange(prevDay())}
            >
              <Icon name='SolidChevronLeft' />
            </Button>
          </GridCell>

          <GridCell width='70%' valign='middle'>
            <DatePickerStyled
              data-testid='date-nav-date'
              onClick={toggleCalendar}
              data-js-isopen={showCalendar}
            >
              <LegacyIcon
                type='calendar'
                color={showCalendar ? hexColors.white : hexColors.black}
                size='2.2rem'
              />

              {dateString}

              <Icon name={showCalendar ? 'SolidChevronUp' : 'SolidChevronDown'} />
            </DatePickerStyled>
          </GridCell>

          <GridCell width='15%' valign='middle' align='left'>
            <Button
              data-testid='date-nav-next'
              color='secondary'
              type='button'
              onClick={() => onDateChange(nextDay())}
            >
              <Icon name='SolidChevronRight' />
            </Button>
          </GridCell>
        </GridRow>
      </Grid>

      <Popover isOpen={showCalendar}>
        <CalendarDatePicker value={date} onChange={onValueChange} />
      </Popover>
    </div>
  )
}

const Popover = ({
  isOpen,
  children,
}: {
  isOpen: boolean
  children: JSX.Element | null
}): null | JSX.Element => {
  if (!isOpen) {
    return null
  }
  return children
}

// ======
// Styles
// ======

const DatePickerStyled = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: spacing.smx2,
  boxSizing: 'border-box',
  fontSize: '1.4rem',
  fontWeight: 'bold',
  padding: '0.8rem 1rem',
  margin: '0 1rem',
  textAlign: 'center',
  position: 'relative',
  transition: 'background-color 0.25s ease-in-out',
  backgroundPosition: 'right 1rem top 50%',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1rem',

  backgroundColor: hexColors.white,
  color: hexColors.black,
  border: `1px solid ${hexColors.veryLightGrey}`,

  '&[data-js-isopen="true"]': {
    backgroundColor: hexColors.chocolate,
    color: hexColors.white,
    border: `1px solid ${hexColors.chocolate}`,
  },
})
